import { Box, Button } from '@mui/material';
import React from 'react';
import { notesApi } from '../../utils/httpUtils';
import NotesCard from './components/NotesCard';
import WelcomeBlock from './components/WelcomeBlock';
import { periods } from './components/periods';

export const QualityList = () => {
  const [view, setView] = React.useState('real-time');
  const [filters, setFilters] = React.useState({
    period: 'Últimos 7 días',
    section: null,
    search: null,
    start_date: null,
    end_date: null,
  });
  const [dateErrors, setDateErrors] = React.useState({
    start_date: '',
    end_date: '',
  });
  const [currentPage, setCurrentPage] = React.useState(1);
  const [notes, setNotes] = React.useState([]);
  const [sections, setSections] = React.useState([]);
  const [totalPages, setTotalPages] = React.useState(1);
  const [loading, setLoading] = React.useState(false);
  const [sortBy, setSortBy] = React.useState('quality');
  const [sortOrder, setSortOrder] = React.useState('desc');
  const notesPerPage = 15;
  // Load sections on component mount
  React.useEffect(() => {
    const loadSections = async () => {
      try {
        const sectionsData = await notesApi.getSections();
        setSections(sectionsData);
      } catch (error) {
        console.error('Error loading sections:', error);
      }
    };
    loadSections();
  }, []);

  // Load notes when view, filters, or page changes
  React.useEffect(() => {
    const getDaysFromPeriod = period => {
      switch (period) {
        case 'Ayer':
          return 'yesterday';
        case 'Últimos 7 días':
          return '7_days';
        case 'Últimos 28 días':
          return '28_days';
        case 'Personalizado':
          return 'custom';
        default:
          return null;
      }
    };

    const loadNotes = async () => {
      setLoading(true);
      try {
        // Mapear los nombres de las columnas del frontend a los nombres que espera la API
        const mapSortByToApi = sortField => {
          switch (sortField) {
            case 'quality':
              return 'calidad';
            case 'title':
              return 'titulo';
            case 'author':
              return 'autor';
            case 'date':
              return 'fecha';
            case 'category':
              return 'categoria';
            default:
              return 'calidad';
          }
        };

        const params = {
          page: currentPage,
          per_page: notesPerPage,
          seccion: filters.section,
          search: typeof filters.search === 'string' ? filters.search : null,
        };

        // Agregar parámetros de ordenamiento según la vista
        if (view === 'real-time') {
          // Tiempo real: solo título y calidad
          if (sortBy === 'quality' || sortBy === 'title') {
            params.order_by = mapSortByToApi(sortBy);
            params.order_direction = sortOrder;
          }
        } else if (view === 'historical') {
          // Histórico: todos los campos disponibles
          params.order_by = mapSortByToApi(sortBy);
          params.order_direction = sortOrder;
        }

        let response;
        if (view === 'real-time') {
          response = await notesApi.getRealTimeNotes(params);
        } else {
          const date_range = getDaysFromPeriod(filters.period);
          if (date_range) {
            params.date_range = date_range;
            // If it's a custom period, add start_date and end_date
            if (date_range === 'custom') {
              if (filters.start_date) {
                params.start_date = filters.start_date;
              }
              if (filters.end_date) {
                params.end_date = filters.end_date;
              }
            }
          }
          response = await notesApi.getHistoricalNotes(params);
        }

        setNotes(response.data || []);
        const ntotal = response?.meta?.total || response?.total || 0;
        setTotalPages(Math.ceil((ntotal || 0) / notesPerPage));
      } catch (error) {
        console.error('Error loading notes:', error);
        setNotes([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, [view, filters, currentPage, notesPerPage, sortBy, sortOrder]);

  const handleViewChange = newView => {
    setView(newView);
    setCurrentPage(1);
    // Reset period filter when switching to real-time
    if (newView === 'real-time') {
      setFilters(prev => ({ ...prev, period: null }));
    }
  };

  const handleSearchChange = value => {
    setFilters(prev => ({ ...prev, search: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handlePeriodChangeWithReset = newPeriod => {
    setFilters(prev => ({
      ...prev,
      period: newPeriod,
      // Clear dates when not custom period
      start_date: newPeriod === 'Personalizado' ? prev.start_date : null,
      end_date: newPeriod === 'Personalizado' ? prev.end_date : null,
    }));
    // Clear date errors when changing period
    setDateErrors({ start_date: '', end_date: '' });
    setCurrentPage(1);
  };

  const handleDateChange = (field, value) => {
    // Clear previous errors
    setDateErrors(prev => ({ ...prev, [field]: '' }));

    // Update the filter value
    const newFilters = { ...filters, [field]: value };

    // Validate dates if both are present
    if (newFilters.start_date && newFilters.end_date) {
      const startDate = new Date(newFilters.start_date);
      const endDate = new Date(newFilters.end_date);

      if (endDate < startDate) {
        setDateErrors(prev => ({
          ...prev,
          end_date: 'La fecha de fin no puede ser menor que la fecha de inicio',
        }));
        return; // Don't update filters if validation fails
      }
    }

    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleSectionChangeWithReset = newSection => {
    setFilters(prev => ({ ...prev, section: newSection }));
    setCurrentPage(1);
  };

  const handleSortChange = column => {
    if (sortBy === column) {
      // Si se hace clic en la misma columna, cambiar el orden
      setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      // Si se hace clic en una nueva columna, establecer descendente por defecto
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1); // Resetear a la primera página
  };

  return (
    <Box sx={{ width: '100%', padding: '1rem' }}>
      <WelcomeBlock />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          <Button
            variant={view === 'real-time' ? 'contained' : 'outlined'}
            onClick={() => handleViewChange('real-time')}
          >
            <span>Tiempo real</span>
          </Button>
          <Button
            variant={view === 'historical' ? 'contained' : 'outlined'}
            onClick={() => handleViewChange('historical')}
          >
            <span>Histórico</span>
          </Button>
        </Box>
      </Box>
      {view === 'real-time' ? (
        <NotesCard
          title="Publicadas hoy"
          filters={filters}
          onSectionChange={handleSectionChangeWithReset}
          onSearchChange={handleSearchChange}
          onPageChange={handlePageChange}
          paginatedNotes={notes}
          totalPages={totalPages}
          currentPage={currentPage}
          sections={sections}
          notes={notes}
          loading={loading}
          historical={false}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        />
      ) : (
        view === 'historical' && (
          <NotesCard
            filters={filters}
            dateErrors={dateErrors}
            onSectionChange={handleSectionChangeWithReset}
            onSearchChange={handleSearchChange}
            onPeriodChange={handlePeriodChangeWithReset}
            onDateChange={handleDateChange}
            onPageChange={handlePageChange}
            paginatedNotes={notes}
            totalPages={totalPages}
            currentPage={currentPage}
            sections={sections}
            notes={notes}
            historical={true}
            periods={periods}
            showPeriodFilter={true}
            title="Histórico"
            loading={loading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />
        )
      )}
    </Box>
  );
};
